import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useMembers } from 'chat/members/queries/list'
import { MentionsScreenProps } from 'home/stack/types'
import React, { useCallback, useLayoutEffect, useState } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { toggleItemInArray } from 'shared/utils'
import { useNonDeletedMessages } from '../queries/list'
import { useCreateMentions } from '../queries/mentions'
import { MentionsView } from './view'

export function MentionsScreen({ navigation, route }: MentionsScreenProps) {
  const { theme } = useTheme()
  const { conversationId, messageId } = route.params
  const useCreateMentionsMutation = useCreateMentions()
  const {
    data: initialSelectedUserIds,
    isLoading: isMessagesLoading,
    error: messagesError,
  } = useNonDeletedMessages(
    data =>
      data.messages
        .find(message => message.id === messageId)
        ?.mentions?.map(mention => mention.user.id) ?? []
  )
  const [selectedUserIds, setSelectedUserIds] = useState(
    initialSelectedUserIds ?? []
  )

  const onSave = useCallback(() => {
    useCreateMentionsMutation
      .mutateAsync({ conversationId, messageId, userIds: selectedUserIds })
      .catch(e => null)
    navigation.goBack()
  }, [
    conversationId,
    messageId,
    navigation,
    selectedUserIds,
    useCreateMentionsMutation,
  ])

  useLayoutEffect(() => {
    showHeaderTitle(navigation, t('mentions'))
    showHeaderButton({
      navigation,
      theme,
      title: t('Cancel'),
      onPress: () => {
        navigation.goBack()
      },
    })
    showHeaderButton({
      navigation,
      theme,
      title: t('done'),
      onPress: onSave,
      position: 'right',
    })
  }, [navigation, onSave, theme])

  const members = useMembers(conversationId)

  const memberList = members.data?.flat()

  const toggleSelectedMember = useCallback(
    (id: string) => {
      setSelectedUserIds(toggleItemInArray(selectedUserIds, id))
    },
    [selectedUserIds]
  )

  return (
    <StatusBoundary isLoading={isMessagesLoading} error={messagesError}>
      <MentionsView
        members={memberList ?? []}
        selectedMemberIds={selectedUserIds}
        onSelectMember={toggleSelectedMember}
      />
    </StatusBoundary>
  )
}
